import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAccessoryById,
  archiveAccessoryRpc,
  unarchiveAccessoryRpc,
} from '@/services/accessories';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Accessory } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { AccessoryForm } from './AccessoryForm';
import { StockAdjustmentModal } from './StockAdjustmentModal';
import { ArchiveRecordModal } from '@/components/common/ArchiveRecordModal';
import { useAuth } from '@/context/AuthContext';
import {
  Package,
  AlertTriangle,
  Edit2,
  Trash2,
  Sliders,
  Loader2,
  Archive,
  RotateCcw,
} from 'lucide-react';

interface AccessoryDetailProps {
  accessoryId: string;
  onClose: () => void;
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const AccessoryDetail: React.FC<AccessoryDetailProps> = ({ accessoryId, onClose }) => {
  const { hasPermission, role } = useAuth();
  const isOwner = role === 'owner';
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [adjustMode, setAdjustMode] = useState<'adjust' | 'remove'>('adjust');
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  const { data: accessory, isLoading, error, refetch } = useQuery<Accessory>({
    queryKey: queryKeys.accessories.detail(accessoryId),
    queryFn: () => fetchAccessoryById(accessoryId),
  });

  const handleUnarchive = async () => {
    if (!accessory) return;
    try {
      setUnarchiveLoading(true);
      setUnarchiveError(null);
      await unarchiveAccessoryRpc(accessory.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void refetch();
    } catch (err: unknown) {
      setUnarchiveError(parseDatabaseError(err));
    } finally {
      setUnarchiveLoading(false);
    }
  };

  if (showEditForm && accessory) {
    return (
      <Modal isOpen title={`Edit Catalogue — ${accessory.name}`} onClose={() => setShowEditForm(false)}>
        <AccessoryForm
          accessory={accessory}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
            void refetch();
            setShowEditForm(false);
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>
    );
  }

  const isArchived = !!accessory?.archived_at;

  return (
    <Modal isOpen title={`Accessory Details — ${accessory?.name || '...'}`} onClose={onClose}>
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}
      {unarchiveError && <ErrorAlert message={unarchiveError} />}

      {accessory && !isLoading && (
        <div className="space-y-4">
          {/* Archived Banner */}
          {isArchived && (
            <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg flex items-start justify-between gap-2 text-xs text-slate-800">
              <div className="flex items-start space-x-2">
                <Archive className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold uppercase tracking-wider block">Archived Item (Hidden from Active Operations)</span>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Reason: <em>{accessory.archive_reason || 'No explanation provided'}</em> • Archived on {formatDate(accessory.archived_at!)}
                  </p>
                </div>
              </div>
              {isOwner && (
                <button
                  type="button"
                  onClick={handleUnarchive}
                  disabled={unarchiveLoading}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded hover:bg-slate-50 transition-colors shrink-0"
                >
                  {unarchiveLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  <span>Restore</span>
                </button>
              )}
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green shrink-0">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  <span className="px-2 py-0.5 bg-brand-bg border border-brand-border text-brand-black text-[11px] font-semibold rounded">
                    {accessory.category}
                  </span>
                  {isArchived && (
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-800 text-xs font-bold rounded-full uppercase">
                      Archived
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-brand-black mt-1">{accessory.name}</h3>
                <p className="text-xs text-brand-muted">SKU: {accessory.sku || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
              {canViewCosts && !isArchived && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setAdjustMode('adjust');
                      setShowAdjustModal(true);
                    }}
                    className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-bg text-brand-black text-xs font-semibold rounded-md border border-brand-border hover:bg-white transition-colors"
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    <span>Adjust Stock</span>
                  </button>
                  {accessory.quantity > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setAdjustMode('remove');
                        setShowAdjustModal(true);
                      }}
                      className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-brand-red border border-red-200 text-xs font-semibold rounded-md hover:bg-red-100 transition-colors"
                      title="Remove or write-off stock with audit reason"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Remove Stock</span>
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
              {isOwner && !isArchived && (
                <button
                  type="button"
                  onClick={() => setShowArchiveModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-100 text-slate-700 border border-slate-300 text-xs font-semibold rounded-md hover:bg-slate-200 transition-colors"
                  title="Archive / Hide this item from catalogue"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive Item</span>
                </button>
              )}
            </div>
          </div>

          {/* Stock & Reorder status box */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-brand-bg p-3 rounded-lg border border-brand-border">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">In Stock</p>
              <p className={`text-base font-bold ${accessory.quantity <= accessory.reorder_level ? 'text-brand-red' : 'text-brand-black'}`}>
                {accessory.quantity} units
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Reorder Alert Level</p>
              <p className="text-sm font-semibold text-brand-black">{accessory.reorder_level} units</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Selling Price</p>
              <p className="text-base font-bold text-brand-green">{formatCurrency(accessory.selling_price)}</p>
            </div>
          </div>

          {accessory.quantity <= accessory.reorder_level && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2 text-xs text-brand-red font-semibold">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Low Stock Alert: Current stock ({accessory.quantity}) is at or below the reorder threshold ({accessory.reorder_level}).</span>
            </div>
          )}

          {/* Cost & Supplier info for owner/manager */}
          {canViewCosts && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-2.5 bg-white rounded border border-brand-border">
                <p className="font-semibold text-brand-muted">Average Cost Price</p>
                <p className="font-medium text-brand-black">{formatCurrency(accessory.average_cost)}</p>
              </div>
              <div className="p-2.5 bg-white rounded border border-brand-border">
                <p className="font-semibold text-brand-muted">Supplier</p>
                <p className="font-medium text-brand-black">{accessory.supplier?.name || '—'}</p>
              </div>
            </div>
          )}

          {/* Notes */}
          {accessory.notes && (
            <div className="p-3 bg-brand-bg rounded border border-brand-border text-xs">
              <p className="font-semibold text-brand-black mb-0.5">Notes</p>
              <p className="text-brand-muted break-words">{accessory.notes}</p>
            </div>
          )}

          {/* Archive Modal for Owner */}
          {showArchiveModal && (
            <ArchiveRecordModal
              isOpen={showArchiveModal}
              onClose={() => setShowArchiveModal(false)}
              entityType="Accessory"
              identifier={`${accessory.sku || 'No SKU'} • ${accessory.name}`}
              subIdentifier={`Category: ${accessory.category || 'General'} • Current Stock: ${accessory.quantity}`}
              warnings={
                accessory.quantity > 0
                  ? [`Current stock is ${accessory.quantity} units. Stock must be adjusted or removed to 0 before archiving.`]
                  : []
              }
              onConfirm={async (reason) => {
                await archiveAccessoryRpc({ accessoryId: accessory.id, reason });
                setShowArchiveModal(false);
                void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
                void refetch();
              }}
            />
          )}

          {/* Stock Adjustment Modal */}
          {showAdjustModal && (
            <StockAdjustmentModal
              accessory={accessory}
              isOpen={showAdjustModal}
              initialMode={adjustMode}
              onClose={() => {
                setShowAdjustModal(false);
                void refetch();
              }}
            />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors">
          Close
        </button>
      </div>
    </Modal>
  );
};
